<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <h4 class="modal-card-title">
        Add Items From {{ isInventory ? 'Inventory' : 'Production'}}
      </h4>
    </header>
    <section class="modal-card-body is-size-2 is-marginless">
      <div class="columns">
        <div class="column">
          <h3 class="has-text-black-bis has-text-weight-bold is-size-3 mb-2">
            {{ isInventory ? 'Inventory' : 'Production'}} Location
          </h3>
          <span>{{ card.delivery.currentLocation.name }}</span>
        </div>
        <div class="column">
          <h3 class="has-text-black-bis has-text-weight-bold is-size-3  mb-2">
            Add Items to Delivery
          </h3>
          <div v-if="isDeliveryTab">
            <span>{{ card.name }}</span>
          </div>
          <div v-else>
            <mf-multi-select v-model="targetDelivery"
              track-by="_id" label="name"
              openDirection="below"
              placeholder="Select Target Delivery"
              :options="targetDeliveries"
              @update:modelValue="(d) => targetDelivery = d">
            </mf-multi-select>
          </div>
        </div>
      </div>
      <search-bar
        placeholder="Search"
        :shouldEmit="shouldEmit"
        @search="getSearchValue($event)"
      ></search-bar>
      <div class="table-container">
        <mf-table
          :tableProps="tableProps"
          :loadData="loadData"
          :hideGutter="true"
          ref="itemInventoryTable">
          <template v-slot:select-catalog="{ rowData: item }">
           <o-checkbox v-model="item.selected"
            @update:modelValue="toggleItem(item)">
           </o-checkbox>
          </template>
          <template v-if="isInventory" v-slot:qty-needed="{ rowData : item }">
            <qty-input v-if="item.selected"
              v-model.number="item.qtyNeeded"
              :value="item.qtyNeeded" @update:value="itemQtyChanged($event, item)"
              :roundTo="4" :max="item.available > QtyOptions.QTY_MAX ? QtyOptions.QTY_MAX : item.available"
            >
            </qty-input>
          </template>
        </mf-table>
      </div>
    </section>
    <footer class="modal-card-foot is-justify-content-flex-end">
      <button class="button is-outlined" true type="button" @click="cancel()">
        Cancel
      </button>
      <button
        class="button has-background-black-bis"
        type="button"
        :disabled="disableSave"
        @click="saveItems"
      >
       Add Items
      </button>
    </footer>
    <o-loading
      :full-page="false"
      :active="isLoading"
      :can-cancel="true"
    ></o-loading>
  </div>
</template>

<script>

import { computed, defineAsyncComponent, defineComponent, onMounted, reactive, ref, toRefs } from 'vue';
import SearchBar from '@/components/SearchBar.vue';
import addItemsFromInvCols from '@/components/table-cols/addItemsFromInv';
import addItemsFromProdCols from '@/components/table-cols/addItemsFromProdCols';
import { useStore } from 'vuex';
import SupplyChain from '@/models/SupplyChain';
import { cloneDeep, filter, isEmpty, remove, some, unionBy, uniqBy, get } from 'lodash';
import QtyInput from '@/components/fields/QtyInput.vue';
import MfMultiSelect from '@/components/abstract/MfMultiSelect';
import Shipping from '@/models/Shipping';
import GeneralShippingMixin from '@/components/mixins/GeneralShippingMixin';
import QtyOptions from '../utils/QtyOptions';

const MfTable = defineAsyncComponent(() => import('@/components/table-fields/MfTable.vue'));


export default defineComponent({
  name: 'AddItemsToShipping',
  components: {
    'search-bar': SearchBar,
    MfTable,
    QtyInput,
    MfMultiSelect,
  },
  props: {
    isInventory: Boolean,
    card: Object,
    isDeliveryTab: Boolean,
    rowData: Object,
    masterCard: Object,
  },

  setup(props, { emit }) {
    const store = useStore();
    const itemInventoryTable = ref(null);
    const tableProps = props.isInventory ? addItemsFromInvCols: addItemsFromProdCols;
    const { addItemFromInv } = GeneralShippingMixin();
    const state = reactive({
      isLoading: false,
      selectedItems: {},
      searchText: '',
      targetDelivery: null,
      shouldEmit: true,
      prodItems: [],
      targetDeliveries: [],
    })

    const loadData = async (commonParams) => {
      let data = {};
      if (props.isInventory) {
        const params = {
          ...commonParams,
          inStock: 'hideOutofStock',
          locs: [props.card.delivery.currentLocation._id],
          projectIds: [props.card.project._id],
          search: state.searchText,
        };
        data = await SupplyChain.inventory({ params, ...params }, false, true);
        data.data = data.data.filter((item) => {
          if (item.available > 0) return item;
        });
      } else {
        const commonStockProject = get(store.state.queryParams, 'commonStockProject', {})
        const destinationProject = get(props.masterCard, 'delivery.deliveryProject', {})
        let isCommonStock = false;
        if (!isEmpty(destinationProject) && ['m', 's-m'].includes(props.masterCard.shipType)) {
          isCommonStock = commonStockProject._id === destinationProject._id;
        }
        data = await Shipping.getCompletedPOItems({
          projectId: props.card.project._id,
          locationId: props.card.delivery.currentLocation._id,
          showCatItems: isCommonStock,
          search: state.searchText,
        });
      }
      if (isEmpty(data)) {
        data.data = [];
      }
      return data;
    };

    const toggleItem = (item) => {
      const itemId = item.catId;
      let val;
      if (props.isInventory) {
        if (itemId === '' || isEmpty(state.selectedItems[itemId])) {
          item.qtyNeeded = 1;
          val = { value: true, item };
        } else {
          val = { value: !state.selectedItems[itemId].value, item };
        }
        state.selectedItems[itemId] = val;
      } else {
        if (isEmpty(state.prodItems)) {
          item.isNew = true
        }
        if (!isEmpty(state.prodItems)) {
          for (let prodItem of state.prodItems) {
            if (prodItem.items._id === item.items._id) {
              remove(state.prodItems, (i) => i.items._id === item.items._id);
              delete item.isNew;
            } else {
              item.isNew = true;
            }
          }
        }
        if (item.isNew) {
          state.prodItems.push(item);
        }
      }
    };

    const itemQtyChanged = ((newQty, item) => {
      state.selectedItems[item.catId].item.qtyNeeded = newQty || 1;
    });

    const getSearchValue = async (searchText) => {
      state.searchText = searchText;
      await itemInventoryTable.value.refreshTable();
    };

    const cancel = () => {
      emit('close');
    };

    const disableSave = computed(() => {
      if (props.isInventory) {
        if (props.isDeliveryTab) return !some(Object.values(state.selectedItems), 'value');
        if (!some(Object.values(state.selectedItems), 'value') || (state.targetDelivery === null)) return true;
      } else {
        if (props.isDeliveryTab) return isEmpty(state.prodItems);
        if (isEmpty(state.prodItems) || (state.targetDelivery === null)) return true;
      }
      return false;
    });

    const saveItems = async () => {
      try {
        if (props.isInventory) {
          state.isLoading = true;
          const itemsData = Object.values(state.selectedItems);
          if (!isEmpty(itemsData)) {
            props.isDeliveryTab ? await addItemFromInv(props.rowData, itemsData, props.card._id) : await addItemFromInv(props.card, itemsData, state.targetDelivery._id);
          }
        } else {
          state.isLoading = true;
          // const selectedProdItems = unionBy(state.prodItems, '_id');
          let items = [];
          for (let item of state.prodItems) {
            item.items.moveQuantity = item.items.quantity;
            item.items.availableQty = item.items.quantity;
            item.items.cardId = item._id;
            item.items.runId = item.runId;
            items.push(item.items);
          }
          await Shipping.moveCompletedPOItems({
            fromParentSLId: props.masterCard._id,
            toChildSLId: props.isDeliveryTab ? props.card._id : state.targetDelivery._id,
            items: items,
            projectId: props.card.project._id,
            locationId: props.card.delivery.currentLocation._id,
          })
        }
      } catch (error) {
        console.log('error', error);
      } finally {
        state.isLoading = false;
        emit('close', { doRefresh: true});
      }
    }

    onMounted(() => {
      state.targetDeliveries = filter(props.card.partialShipments, (s) =>  ['not-started','un-scheduled'].includes(s.status));
    });

    return {
      ...toRefs(state),
      itemInventoryTable,
      cancel,
      tableProps,
      loadData,
      toggleItem,
      itemQtyChanged,
      getSearchValue,
      saveItems,
      disableSave,
      QtyOptions,
    }
  }
})
</script>